import { MdPiece } from "./MessageItem";

export type MentionResolvers = {
  /** Resolve a bare Slack user ID (e.g. "U04QPK9JM28") to a display name. */
  resolveUser?: (userId: string) => string | undefined;
  /** Resolve a Slack usergroup ID (e.g. "S01234567") to a display name. */
  resolveUsergroup?: (groupId: string) => string | undefined;
};

/**
 * Renders a preparsed MdPiece[] to a plain text string suitable for a chip
 * label. Strips markdown syntax, extracts annotations from links, and omits
 * structural elements (tables, footnotes) that don't read well as plain text.
 */
export function ppToChipText(
  pieces: MdPiece[],
  resolvers?: MentionResolvers
): string {
  return pieces.map((p) => pieceToText(p, resolvers)).join("");
}

function pieceToText(p: MdPiece, resolvers?: MentionResolvers): string {
  const v = p.v;

  switch (p.t) {
    case "txt":
      return typeof v === "string" ? v : "";

    case "br":
    case "br2":
      return " ";

    case "hr":
      return " ";

    case "lnk":
      // Slack angle-bracket link: <url|annotation> or <url>
      return typeof v === "string" ? linkText(v, 1) : "";

    case "lx":
      // GitHub markdown link: url|annotation (string) or nested pieces (array)
      if (Array.isArray(v)) return ppToChipText(v as MdPiece[], resolvers);
      return typeof v === "string" ? linkText(v, 0) : "";

    case "url":
      return typeof v === "string" ? v : "";

    case "rk":
    case "ref":
    case "h":
      return typeof v === "string" ? v : "";

    case "u": {
      // Slack user mention — v may be raw ID or Slack's <@U...> format
      if (typeof v !== "string") return "";
      const userId = stripSlackId(v, "@");
      const resolved = resolvers?.resolveUser?.(userId);
      return `@${resolved ?? userId}`;
    }

    case "c": {
      // Slack channel: <#C12345|name> or <#C12345>
      if (typeof v !== "string") return "";
      const inner = v.slice(2, -1); // strip leading <# and trailing >
      // Use handle if present (last segment after |), otherwise use ID
      return "#" + inner.split("|").slice(-1)[0];
    }

    case "e":
      // Emoji :name: — return as-is
      return typeof v === "string" ? v : "";

    case "b": {
      // Slack broadcast: <!here>, <!channel|general>, <!everyone>
      // Use last segment after | — matches SlackMention behaviour
      if (typeof v !== "string") return "";
      const inner = v.startsWith("<!")
        ? v.slice(2, -1).split("|").slice(-1)[0]
        : v;
      return `@${inner}`;
    }

    case "s": {
      // Slack subteam: <!subteam^S01234567|handle> or plain value
      if (typeof v !== "string") return "";
      if (v.startsWith("<!subteam^")) {
        const afterCaret = v.slice(0, -1).split("^")[1] ?? "";
        const [groupId, handle] = afterCaret.split("|");
        const resolved = resolvers?.resolveUsergroup?.(groupId);
        return `@${resolved ?? handle ?? groupId}`;
      }
      return `@${v}`;
    }

    case "cl":
      // Slack inline code: `code` — strip surrounding backticks
      return typeof v === "string" ? v.slice(1, -1) : "";

    case "cx":
    case "code":
      // GitHub inline code: raw string
      return typeof v === "string" ? v : "";

    case "cb": {
      // Slack code block: ```lang\ncode``` — show first content line
      if (typeof v !== "string") return "[code]";
      const body = v.slice(3).replace(/```$/, "").trim();
      const first = body.split("\n")[0].trim();
      return first ? `[${first}]` : "[code]";
    }

    case "cbx": {
      // GitHub code block: raw code content — show first line
      if (typeof v !== "string") return "[code]";
      const first = v.trim().split("\n")[0].trim();
      return first ? `[${first}]` : "[code]";
    }

    case "q":
    case "it":
    case "bld":
    case "stk":
    case "sup":
    case "sub":
    case "mark":
    case "ins":
    case "footnote":
      // Formatting wrappers — recurse into children
      if (Array.isArray(v)) return ppToChipText(v as MdPiece[], resolvers);
      return typeof v === "string" ? v : "";

    case "img":
      // Image — skip (alt text not reliably stored)
      return "";

    case "fx": {
      // File attachment: url|filename or url
      if (typeof v !== "string") return "";
      const pipe = v.indexOf("|");
      return pipe !== -1 ? v.slice(pipe + 1) : "";
    }

    case "fr":
    case "fd":
    case "t":
    case "table":
      // Footnote markers and tables — not useful in chip text
      return "";

    default:
      if (Array.isArray(v)) return ppToChipText(v as MdPiece[], resolvers);
      return typeof v === "string" ? v : "";
  }
}

/**
 * Extracts the display text from a link value.
 * @param link  Raw link string stored in MdPiece.v
 * @param trim  Chars to strip from each end before parsing
 *              (1 for Slack `<url|label>`, 0 for GitHub `url|label`)
 */
function linkText(link: string, trim: number): string {
  const v = trim ? link.slice(trim, -trim) : link;
  const pipe = v.indexOf("|");
  if (pipe === -1) return v;
  const annotation = v.slice(pipe + 1);
  return annotation.length ? annotation : v.slice(0, pipe);
}

/**
 * Strips Slack's angle-bracket ID format.
 * "<@U123>" → "U123", "@name" → "name", "U123" → "U123"
 */
function stripSlackId(raw: string, prefix: string): string {
  if (raw.startsWith(`<${prefix}`)) return raw.slice(prefix.length + 1, -1);
  if (raw.startsWith(prefix)) return raw.slice(prefix.length);
  return raw;
}
